import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { LockKeyhole, MailCheck, ArrowRight, AlertTriangle, Loader2 } from "lucide-react";
import { http, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * AcceptInvite — landing page for `/accept-invite/:token`.
 *
 * Public route (no auth required). Calls GET /api/auth/invite/:token to
 * peek at the invite metadata (so we can prefill the email + role +
 * tenant name), then POSTs name + password to accept. On success the
 * backend issues auth cookies and we redirect to the dashboard.
 */
export default function AcceptInvite() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { reload } = useAuth();

    const [invite, setInvite] = useState(null);
    const [loadErr, setLoadErr] = useState("");
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitErr, setSubmitErr] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await http.get(`/auth/invite/${token}`);
                if (!cancelled) setInvite(data);
            } catch (e) {
                if (!cancelled) setLoadErr(errMsg(e));
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitErr("");
        if (password !== confirm) {
            setSubmitErr("Passwords don't match");
            return;
        }
        if (password.length < 6) {
            setSubmitErr("Password must be at least 6 characters");
            return;
        }
        setSubmitting(true);
        try {
            await http.post(`/auth/invite/${token}/accept`, { name, password });
            await reload();
            navigate("/", { replace: true });
        } catch (err) {
            setSubmitErr(errMsg(err));
        } finally {
            setSubmitting(false);
        }
    };

    if (loadErr) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-zinc-950 p-6">
                <div
                    data-testid="accept-invite-error"
                    className="max-w-md rounded-lg border border-amber-500/30 bg-amber-500/5 p-6 text-amber-100"
                >
                    <div className="mb-2 flex items-center gap-2 font-display text-lg font-bold">
                        <AlertTriangle size={18} /> This invite is no longer valid
                    </div>
                    <div className="text-sm text-amber-200/80">{loadErr}</div>
                    <Link
                        to="/login"
                        data-testid="accept-invite-go-login"
                        className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-amber-200 hover:text-white"
                    >
                        Go to login <ArrowRight size={12} />
                    </Link>
                </div>
            </div>
        );
    }

    if (!invite) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-zinc-950 text-zinc-400">
                <Loader2 className="h-5 w-5 animate-spin" />
            </div>
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-zinc-950 p-6">
            <div className="w-full max-w-md rounded-lg border border-zinc-800 bg-zinc-900/60 p-8 shadow-xl">
                <div className="mb-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                        Accept invite
                    </div>
                    <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-zinc-50">
                        Join {invite.tenant_name || "MoovBytes"}
                    </h1>
                    <p className="mt-1 text-sm text-zinc-400">
                        You've been invited to join as{" "}
                        <span className="rounded bg-blue-500/10 px-1.5 py-0.5 font-mono text-[11px] uppercase tracking-wider text-blue-300 border border-blue-500/30">
                            {invite.role}
                        </span>
                        . Set a password to activate your account.
                    </p>
                </div>

                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                            Email
                        </label>
                        <div className="mt-1 flex items-center gap-2 rounded border border-zinc-800 bg-zinc-950/60 px-3 py-2.5 text-sm text-zinc-300">
                            <MailCheck size={14} className="text-emerald-400" />
                            <span className="font-mono" data-testid="accept-invite-email">{invite.email}</span>
                        </div>
                    </div>

                    <div>
                        <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                            Full name
                        </label>
                        <input
                            data-testid="accept-invite-name-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            placeholder="Jane Doe"
                            className="mt-1 w-full rounded border border-zinc-800 bg-zinc-950/60 px-3 py-2.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-blue-500 focus:outline-none"
                        />
                    </div>

                    <div>
                        <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                            Password
                        </label>
                        <input
                            data-testid="accept-invite-password-input"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={6}
                            className="mt-1 w-full rounded border border-zinc-800 bg-zinc-950/60 px-3 py-2.5 text-sm text-zinc-100 focus:border-blue-500 focus:outline-none"
                        />
                    </div>

                    <div>
                        <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                            Confirm password
                        </label>
                        <input
                            data-testid="accept-invite-confirm-input"
                            type="password"
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            required
                            minLength={6}
                            className="mt-1 w-full rounded border border-zinc-800 bg-zinc-950/60 px-3 py-2.5 text-sm text-zinc-100 focus:border-blue-500 focus:outline-none"
                        />
                    </div>

                    {submitErr && (
                        <div
                            data-testid="accept-invite-submit-error"
                            className="flex items-center gap-2 rounded border border-rose-500/30 bg-rose-500/5 px-3 py-2 text-xs text-rose-200"
                        >
                            <AlertTriangle size={12} /> {submitErr}
                        </div>
                    )}

                    <button
                        type="submit"
                        data-testid="accept-invite-submit-btn"
                        disabled={submitting || !name || !password}
                        className="flex w-full items-center justify-center gap-2 rounded bg-blue-600 py-2.5 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50"
                    >
                        {submitting ? <Loader2 size={14} className="animate-spin" /> : <LockKeyhole size={14} />}
                        Activate account
                    </button>
                </form>

                <div className="mt-4 text-center text-xs text-zinc-500">
                    Invite expires {new Date(invite.expires_at).toLocaleString()}
                </div>
            </div>
        </div>
    );
}
